import React, { useState } from "react";
import * as XLSX from "xlsx";

const norm = (s: any) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const num = (v: any) => parseFloat(String(v == null ? "" : v).replace(/[^0-9.\-]/g, "")) || 0;
const pad2 = (n: string) => n.padStart(2, "0");

function parseDate(v: any): string {
  if (v == null || v === "") return "";
  if (typeof v === "number") { const d = new Date(Math.round((v - 25569) * 86400 * 1000)); return d.toISOString().slice(0, 10); }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/); if (m) return `${m[3]}-${pad2(m[2])}-${pad2(m[1])}`;
  m = s.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/); if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

const get = (k: string): any[] => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
const setKey = (k: string, d: any[]) => localStorage.setItem(k, JSON.stringify(d));

interface SyncLog { id: string; date: string; type: string; added: number; updated: number; flagged: number; by: string; }

export default function PcsSync({ user }: { user?: any }) {
  const [tab, setTab] = useState<"map" | "res" | "meds" | "inc" | "log">("map");
  const [logs, setLogs] = useState<SyncLog[]>(() => get("pcs_sync_log"));
  const [message, setMessage] = useState("");

  const addLog = (type: string, added: number, updated: number, flagged: number) => {
    const l: SyncLog = { id: `sync-${Date.now()}`, date: new Date().toISOString(), type, added, updated, flagged, by: user && user.name ? user.name : "" };
    const next = [l, ...logs]; setLogs(next); setKey("pcs_sync_log", next);
  };

  const readRows = (file: File, cb: (rows: any[]) => void) => {
    const r = new FileReader();
    r.onload = e => { try { const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: "array" }); cb(XLSX.utils.sheet_to_json<any>(wb.Sheets[wb.SheetNames[0]], { defval: "" })); } catch { setMessage("Could not read file."); } };
    r.readAsArrayBuffer(file);
  };

  const mapCols = (row: any, wants: string[]) => {
    const keys = Object.keys(row);
    for (const w of wants) { const k = keys.find(kk => norm(kk).includes(w)); if (k !== undefined) return row[k]; }
    return "";
  };

  const importResidents = (file: File) => readRows(file, rows => {
    const residents = get("residents_records"); const names = new Set(residents.map((r: any) => r.name));
    let added = 0, updated = 0, flagged = 0;
    rows.forEach(row => {
      const name = String(mapCols(row, ["fullname", "name"])).trim(); if (!name) return;
      const data = { room: String(mapCols(row, ["room"])), admissionDate: parseDate(mapCols(row, ["admission", "admitted", "start"])), exitDate: parseDate(mapCols(row, ["leaving", "exit"])), fundingType: String(mapCols(row, ["funding"])), council: String(mapCols(row, ["council", "authority"])), totalFee: String(num(mapCols(row, ["totalfee", "fee"])) || ""), councilPays: String(num(mapCols(row, ["councilpays", "councilshare"])) || ""), residentPays: String(num(mapCols(row, ["residentpays", "customershare"])) || "") };
      if (names.has(name)) { const i = residents.findIndex((r: any) => r.name === name); residents[i] = { ...residents[i], ...Object.fromEntries(Object.entries(data).filter(([, v]) => v)) }; updated++; }
      else { residents.unshift({ id: `res-${Date.now()}-${added}`, name, dob: "", nhsNumber: "", gpName: "", gpPhone: "", familyName: "", familyPhone: "", familyEmail: "", familyAddress: "", address: "", phone: "", email: "", relation: "", legalRep: "", dietary: "", allergies: "", mobility: "", notes: "Imported from PCS", ...data }); names.add(name); added++; }
    });
    setKey("residents_records", residents); addLog("Residents", added, updated, flagged);
    setMessage(`PCS residents sync: ${added} added, ${updated} updated.`);
  });

  const importMeds = (file: File) => readRows(file, rows => {
    const meds = get("meds_register"); const key = (r: string, m: string) => `${r}|${m}`.toLowerCase();
    const idx = new Map(meds.map((m: any) => [key(m.residentName, m.medName), m]));
    let added = 0, updated = 0;
    rows.forEach(row => {
      const resident = String(mapCols(row, ["resident", "name"])).trim(); const med = String(mapCols(row, ["medication", "medicine", "drug"])).trim();
      if (!resident || !med) return;
      const times = String(mapCols(row, ["times", "rounds", "time"])).split(/[;,/\s]+/).filter(Boolean);
      const rec = { residentName: resident, medName: med, dose: String(mapCols(row, ["dose"])), route: String(mapCols(row, ["route"])) || "Oral", times, isCD: /y|1|true/i.test(String(mapCols(row, ["cd", "controlled"]))), isCovert: /y|1|true/i.test(String(mapCols(row, ["covert"]))), startDate: parseDate(mapCols(row, ["start"])) || "", reviewDate: parseDate(mapCols(row, ["review"])) || "" };
      const existing = idx.get(key(resident, med));
      if (existing) { Object.assign(existing, rec); updated++; } else { const n = { id: `med-${Date.now()}-${added}`, ...rec }; meds.unshift(n); idx.set(key(resident, med), n); added++; }
    });
    setKey("meds_register", meds); addLog("Medications", added, updated, 0);
    setMessage(`PCS medication sync: ${added} added, ${updated} updated.`);
  });

  const importIncidents = (file: File) => readRows(file, rows => {
    const nm = get("near_miss_records"); let added = 0;
    rows.forEach(row => {
      const desc = String(mapCols(row, ["description", "detail", "incident"])).trim(); if (!desc) return;
      nm.unshift({ id: `nm-${Date.now()}-${added}`, date: parseDate(mapCols(row, ["date"])) || "", area: String(mapCols(row, ["type", "category"])) || "PCS", description: desc, immediateAction: String(mapCols(row, ["action"])), rootCause: "", learning: "", sharedInMeeting: false, sharedDate: "", reportedBy: "PCS import" });
      added++;
    });
    setKey("near_miss_records", nm); addLog("Incidents", added, 0, 0);
    setMessage(`PCS incident sync: ${added} imported into Near-Miss log.`);
  });

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">PCS Integration Hub</h2>
        <p className="text-sm text-slate-600">Import scheduled exports from PCS (Orbit/myPCS). RMACS upserts without duplicating, flags gaps, and logs every sync as audit evidence.</p>
      </div>
      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}
      <div className="flex flex-wrap gap-2">
        {(["map", "res", "meds", "inc", "log"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-3 py-2 text-sm"}>{t === "map" ? "Data Map" : t === "res" ? "Sync Residents" : t === "meds" ? "Sync Medications" : t === "inc" ? "Sync Incidents" : "Sync Log"}</button>
        ))}
      </div>

      {tab === "map" ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="text-slate-600"><tr><th className="p-2">Data</th><th className="p-2">Golden source</th><th className="p-2">Flows to</th><th className="p-2">Reason</th></tr></thead>
            <tbody>
              <tr className="border-t"><td className="p-2">Care plans, observations, weights</td><td className="p-2">PCS</td><td className="p-2">RMACS wellbeing</td><td className="p-2">Entered at bedside in real time</td></tr>
              <tr className="border-t"><td className="p-2">eMAR & medication list</td><td className="p-2">PCS</td><td className="p-2">RMACS meds + MAR audit</td><td className="p-2">Witnessed at point of care</td></tr>
              <tr className="border-t"><td className="p-2">Risk assessments</td><td className="p-2">PCS</td><td className="p-2">RMACS dependency tool</td><td className="p-2">Clinical judgement at bedside</td></tr>
              <tr className="border-t"><td className="p-2">Incidents</td><td className="p-2">PCS</td><td className="p-2">RMACS near-miss + trends</td><td className="p-2">Captured at point of care</td></tr>
              <tr className="border-t"><td className="p-2">Resident register</td><td className="p-2">PCS</td><td className="p-2">RMACS residents</td><td className="p-2">Admission entered in PCS first</td></tr>
              <tr className="border-t"><td className="p-2">Funding, invoices, reconciliation</td><td className="p-2">RMACS</td><td className="p-2">-</td><td className="p-2">PCS does not do billing</td></tr>
              <tr className="border-t"><td className="p-2">Audits, CQC plan, training, scrutiny</td><td className="p-2">RMACS</td><td className="p-2">-</td><td className="p-2">Governance layer</td></tr>
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500">Principle: never double-key. Each record lives in ONE golden source and is synced, not re-typed. Every sync is logged below as evidence for CQC.</p>
        </div>
      ) : null}

      {tab === "res" ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-2 text-xs text-slate-600">Upload the PCS resident export (Name, Room, Admission, Leaving, Funding, Council, TotalFee, CouncilShare, ResidentShare). Existing residents are updated, new ones added - no duplicates.</p>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => e.target.files && importResidents(e.target.files[0])} className="text-sm" />
        </div>
      ) : null}

      {tab === "meds" ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-2 text-xs text-slate-600">Upload the PCS medication list (Resident, Medication, Dose, Route, Times, CD, Covert, Review). Keeps your RMACS meds register and MAR audit in step with the PCS eMAR.</p>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => e.target.files && importMeds(e.target.files[0])} className="text-sm" />
        </div>
      ) : null}

      {tab === "inc" ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-2 text-xs text-slate-600">Upload the PCS incident/accident export (Date, Resident, Type, Description, Action). Feeds the RMACS Near-Miss log and trend dashboard.</p>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => e.target.files && importIncidents(e.target.files[0])} className="text-sm" />
        </div>
      ) : null}

      {tab === "log" ? (
        <div className="space-y-2">
          {logs.map(l => (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-sm text-slate-800">{new Date(l.date).toLocaleString()} | {l.type} | added {l.added} | updated {l.updated} | flagged {l.flagged} | by {l.by}</div>
            </div>
          ))}
          {logs.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No syncs yet.</div> : null}
        </div>
      ) : null}
    </div>
  );
}